import * as pixel from "../lib/fpixel";
import { countryToCurrency } from "./help/pricing/pricing";
import { PreferredCountryEnum } from "./types";

enum SocialMediaEvent {
  AddToCart = "AddToCart",
  Purchase = "Purchase",
  InitiateCheckout = "InitiateCheckout",
}

enum SocialMediaPlatform {
  Facebook = "Facebook",
  TikTok = "TikTok",
}

const eventMap = {
  [SocialMediaPlatform.Facebook]: {
    [SocialMediaEvent.AddToCart]: "AddToCart",
    [SocialMediaEvent.Purchase]: "Purchase",
    [SocialMediaEvent.InitiateCheckout]: "InitiateCheckout",
  },
  [SocialMediaPlatform.TikTok]: {
    [SocialMediaEvent.AddToCart]: "AddToCart",
    [SocialMediaEvent.Purchase]: "CompletePayment",
    [SocialMediaEvent.InitiateCheckout]: "InitiateCheckout",
  },
};

export type SocialMediaItem = {
  subItemId: number;
  fullName?: string;
  quantity: number;
  totalPrice: number;
};

/**
 * Helper function to push events to Google Tag Manager dataLayer
 */
const pushToDataLayer = (eventData: Record<string, any>) => {
  if (typeof window !== "undefined" && (window as any).dataLayer) {
    (window as any).dataLayer.push(eventData);
  }
};

export class SocialMedia {
  /**
   * Triggers the "Add to Cart" event for specified social media platforms.
   *
   * The "Add to Cart" event is when a user adds an item to the cart.
   *
   * @param itemToAdd - An object containing details of the item to add to the cart.
   * @param itemToAdd.subItemId - The unique identifier for the sub-item.
   * @param itemToAdd.fullName - The full name of the item.
   * @param itemToAdd.quantity - The quantity of the item to add.
   * @param itemToAdd.totalPrice - The total price of the item.
   * @param country - The preferred country to use for currency conversion.
   * @param eventId - Shared with the backend's Conversions API copy so Meta
   * deduplicates the pair into one event. Omitted only where no server event
   * will follow.
   */
  public static addToCartEvent(
    itemToAdd: SocialMediaItem,
    country: PreferredCountryEnum | null | undefined,
    eventId?: string
  ) {
    country ||= PreferredCountryEnum.CA;
    // Facebook Pixel - always sent from the browser. It used to be suppressed
    // whenever the Conversion API was enabled, which removed the only side that
    // actually holds fbc/fbp and so destroyed attribution for those restaurants.
    if (eventMap[SocialMediaPlatform.Facebook][SocialMediaEvent.AddToCart]) {
      pixel.event(
        eventMap[SocialMediaPlatform.Facebook][SocialMediaEvent.AddToCart],
        {
          contents: [
            {
              id: itemToAdd.subItemId,
              quantity: itemToAdd.quantity,
            },
          ],
          content_type: "product",
          value: itemToAdd.totalPrice,
          currency: countryToCurrency(country),
          ...(eventId ? { event_id: eventId } : {}),
        },
        // Meta deduplicates browser/CAPI events only via the fbq eventID
        // parameter — event_id inside custom data is not used for dedup.
        eventId ? { eventID: eventId } : undefined
      );
    }

    // TikTok Pixel
    if (eventMap[SocialMediaPlatform.TikTok][SocialMediaEvent.AddToCart]) {
      pixel.tiktokEvent(
        eventMap[SocialMediaPlatform.TikTok][SocialMediaEvent.AddToCart],
        {
          contents: [
            {
              content_id: itemToAdd.subItemId,
              content_name: itemToAdd.fullName,
              quantity: itemToAdd.quantity,
              price: itemToAdd.totalPrice,
            },
          ],
          content_type: "product",
          value: itemToAdd.totalPrice,
          currency: countryToCurrency(country),
        }
      );
    }

    pushToDataLayer({
      event: "add_to_cart",
      ecommerce: {
        currency: countryToCurrency(country),
        value: itemToAdd.totalPrice,
        items: [
          {
            item_id: itemToAdd.subItemId.toString(),
            item_name: itemToAdd.fullName || "",
            price: itemToAdd.totalPrice,
            quantity: itemToAdd.quantity,
          },
        ],
      },
    });
  }

  /**
   * Triggers purchase events for various social media platforms.
   *
   * The "Purchase" event is when a user completes an order and reaches the order confirmation screen.
   *
   * @param totalPrice - The total price of the purchase.
   * @param country - The preferred country to use for currency conversion.
   * @param items - An optional array of `SocialMediaItem` representing the items purchased.
   * @param shouldSendFromFrontend - Whether to send Facebook Pixel events from frontend (always true).
   */
  public static purchaseEvent(
    eventId: string,
    totalPrice: number,
    country: PreferredCountryEnum | null | undefined,
    items?: SocialMediaItem[],
    shouldSendFromFrontend: boolean = true
  ) {
    country ||= PreferredCountryEnum.CA;

    // Facebook Pixel - only send if shouldSendFromFrontend is true (when Conversion API is enabled)
    if (
      shouldSendFromFrontend &&
      eventMap[SocialMediaPlatform.Facebook][SocialMediaEvent.Purchase]
    ) {
      pixel.event(
        eventMap[SocialMediaPlatform.Facebook][SocialMediaEvent.Purchase],
        {
          contents: items?.map((item) => ({
            id: item.subItemId,
            quantity: item.quantity,
          })),
          num_items: items?.length,
          content_type: "product",
          value: totalPrice,
          currency: countryToCurrency(country),
          ...(eventId ? { event_id: eventId } : {}),
        },
        // Meta deduplicates browser/CAPI events only via the fbq eventID
        // parameter — event_id inside custom data is not used for dedup.
        eventId ? { eventID: eventId } : undefined
      );
    }

    // TikTok Pixel
    if (eventMap[SocialMediaPlatform.TikTok][SocialMediaEvent.Purchase]) {
      pixel.tiktokEvent(
        eventMap[SocialMediaPlatform.TikTok][SocialMediaEvent.Purchase],
        {
          value: totalPrice,
          currency: countryToCurrency(country),
        }
      );
    }

    pushToDataLayer({
      event: "purchase",
      ecommerce: {
        transaction_id: eventId,
        value: totalPrice,
        currency: countryToCurrency(country),
        items:
          items?.map((item) => ({
            item_id: item.subItemId.toString(),
            item_name: item.fullName || "",
            price: item.totalPrice,
            quantity: item.quantity,
          })) || [],
      },
    });
  }

  /**
   * Fires the purchase event at most once per receipt, even across page
   * refreshes / revisits of the confirmation screen, using localStorage as
   * the guard. When storage is unavailable (private mode), the event is
   * still sent and Meta's eventID-based deduplication covers repeats.
   *
   * Accepts the same arguments as {@link purchaseEvent}.
   */
  public static purchaseEventOnce(
    eventId: string,
    totalPrice: number,
    country: PreferredCountryEnum | null | undefined,
    items?: SocialMediaItem[],
    shouldSendFromFrontend: boolean = true
  ) {
    const firedKey = `purchaseEventFired_${eventId}`;

    try {
      if (localStorage.getItem(firedKey)) {
        return;
      }
    } catch {
      // localStorage unavailable — fall through and send the event.
    }

    SocialMedia.purchaseEvent(
      eventId,
      totalPrice,
      country,
      items,
      shouldSendFromFrontend
    );

    try {
      localStorage.setItem(firedKey, "1");
    } catch {
      // Ignore storage failures — the event was still sent once.
    }
  }

  /**
   * Initiates the checkout event for social media platforms.
   *
   * This method triggers the checkout event for Facebook and TikTok pixels,
   * sending relevant data such as total price, country, and items.
   *
   * @param totalPrice - The total price of the items being checked out.
   * @param country - The preferred country to use for currency conversion.
   * @param items - An optional array of SocialMediaItem objects representing the items being checked out.
   * @param eventId - Shared with the backend's Conversions API copy so Meta
   * deduplicates the pair into one event. Omitted only where no server event
   * will follow.
   */
  public static initiateCheckoutEvent(
    totalPrice: number,
    country: PreferredCountryEnum | null | undefined,
    items?: SocialMediaItem[],
    eventId?: string
  ) {
    country ||= PreferredCountryEnum.CA;

    // Facebook Pixel - always sent from the browser, same reasoning as AddToCart.
    if (
      eventMap[SocialMediaPlatform.Facebook][SocialMediaEvent.InitiateCheckout]
    ) {
      pixel.event(
        eventMap[SocialMediaPlatform.Facebook][
          SocialMediaEvent.InitiateCheckout
        ],
        {
          contents: items?.map((item) => ({
            id: item.subItemId,
            quantity: item.quantity,
          })),
          num_items: items?.length,
          content_type: "product",
          value: totalPrice,
          currency: countryToCurrency(country),
          ...(eventId ? { event_id: eventId } : {}),
        },
        // Meta deduplicates browser/CAPI events only via the fbq eventID
        // parameter -- event_id inside custom data is not used for dedup.
        eventId ? { eventID: eventId } : undefined
      );
    }

    // TikTok Pixel
    if (
      eventMap[SocialMediaPlatform.TikTok][SocialMediaEvent.InitiateCheckout]
    ) {
      pixel.tiktokEvent(
        eventMap[SocialMediaPlatform.TikTok][SocialMediaEvent.InitiateCheckout],
        {
          value: totalPrice,
          currency: countryToCurrency(country),
        }
      );
    }

    pushToDataLayer({
      event: "begin_checkout",
      ecommerce: {
        currency: countryToCurrency(country),
        value: totalPrice,
        items:
          items?.map((item) => ({
            item_id: item.subItemId.toString(),
            item_name: item.fullName || "",
            price: item.totalPrice,
            quantity: item.quantity,
          })) || [],
      },
    });
  }
}
